/**
 * @module ui/templates/election/riding-candidates-table
 */
import { getProp } from "utils/utils";

import tgamConfig from "config/tgam";

/**
 * Riding candidates table template
 * @param {Object} data - Data object
 * @param {Number} numberOfParties - The number of parties that are running candidates in the riding
 * @param {Array} data.candidates - Information about all of the candidates in the riding
 * @param {Object} data.incumbentCandidate - The riding's incumbent candidate
 * @param {Object} data.leadingCandidate - The riding's leading candidate
 * @param {Object} data.electedCandidate - The riding's elected candidate
 * @returns {String} HTML markup representing the riding candidates table
 */
export default function ridingCandidatesTableTemplate({ numberOfParties, candidates, incumbentCandidate, leadingCandidate, electedCandidate }) {
  const incumbentIcon = `<span class="c-indicator-icon c-indicator-icon--rerun">
    <svg class="c-indicator-icon__art" role="img" pointer-events="none" aria-label="rerun icon, representing incumbent">
      <use xlink:href="${tgamConfig.env.svgSpritePath}#icon-rerun"></use>
    </svg>
    </span>`;

  const rows = candidates.map(candidate => {
    const isIncumbent = candidate.id === getProp(incumbentCandidate, "id");
    const isLeading = candidate.id === getProp(leadingCandidate, "id");
    const isElected = candidate.id === getProp(electedCandidate, "id");
    const candidatePartyAbbrName = candidate.partyAbbrName === "PP" ? "PPC" : candidate.partyAbbrName;

    let rowClass = "";
    if (isElected) {
      rowClass = " c-election-table__body-row--winner";
    } else if (isLeading) {
      rowClass = " c-election-table__body-row--leading";
    }

    return `
      <tr class="c-election-table__body-row c-election-table__body-row--${candidate.partyClassName}${rowClass}">
        <td class="c-election-table__body-cell c-election-table__body-cell--party-full">
          <span class="c-election-table__body-cell-content">${candidate.partyDisplayName}</span>
        </td>
        <td class="c-election-table__body-cell c-election-table__body-cell--party-abbr">
          <span class="c-election-table__body-cell-content"><abbr class="c-election-table__abbr" title="${candidate.partyDisplayName}">${candidatePartyAbbrName}</abbr></span>
        </td>
        <td class="c-election-table__body-cell c-election-table__body-cell--candidate">
          <span class="c-election-table__body-cell-content">${candidate.name}${isIncumbent ? incumbentIcon : ""}</span>
        </td>
        <td class="c-election-table__body-cell c-election-table__body-cell--votes">
          <span class="c-election-table__body-cell-content">${new Intl.NumberFormat("en-US").format(candidate.votes)}</span>
        </td>
        <td class="c-election-table__body-cell c-election-table__body-cell--percentage">
          <span class="c-election-table__body-cell-content">${candidate.percentageOfVotesWon}%</span>
        </td>
      </tr>
    `;
  }).join("");

  return `
    <div class="c-election-table c-election-table--riding-candidate">
      <table class="c-election-table__table">
        <thead>
          <tr class="c-election-table__head-row">
            <th class="c-election-table__head-cell c-election-table__head-cell--party-full" scope="col">
              <span class="c-election-table__head-cell-content">Party</span>
            </th>
            <th class="c-election-table__head-cell c-election-table__head-cell--party-abbr" scope="col">
              <span class="c-election-table__head-cell-content">Party</span>
            </th>
            <th class="c-election-table__head-cell c-election-table__head-cell--candidate" scope="col">
              <span class="c-election-table__head-cell-content">Candidate</span>
            </th>
            <th class="c-election-table__head-cell c-election-table__head-cell--votes" scope="col">
              <span class="c-election-table__head-cell-content">Votes</span>
            </th>
            <th class="c-election-table__head-cell c-election-table__head-cell--percentage" scope="col">
              <span class="c-election-table__head-cell-content">%</span>
            </th>
          </tr>
        </thead>
        <tbody>
          ${rows}
        </tbody>
      </table>
      <div class="c-election-table__notes">
        <p class="c-election-table__note">${incumbentIcon}Incumbent</p>
        <p class="c-election-table__note">Showing top ${candidates.length} of ${numberOfParties}</p>
      </div>
    </div>
  `;
}
